import React, { PropsWithChildren, useState } from 'react'
import { createContext } from 'lib/createContext'
import { useDocumentSignatureTemplatesQuery } from 'queries/company'
import { useCompanyDetailContextValue } from '../../../context/CompanyDetailProvider'
import { useCompanyDetailWorkflowsContextValue } from '../../../context/CompanyDetailWorkflowProvider'

const PAGE_SIZE = 50

type CompanyFilesContextState = {
  state: {
    files: NonNullable<
      ReturnType<typeof useDocumentSignatureTemplatesQuery>['data']
    >['files']
    totalPages: number
    totalRows: number
    page: number
    isPending: boolean
    isError: boolean
    isFetching: boolean
  }
  actions: {
    setPage: React.Dispatch<React.SetStateAction<number>>
    refetch: ReturnType<typeof useDocumentSignatureTemplatesQuery>['refetch']
    openUpload: () => void
    openEditSchema: ReturnType<
      typeof useCompanyDetailWorkflowsContextValue
    >['editCompanyFileSchemaFlow']['open']
  }
}

const [Context, useCompanyFilesContextValue] =
  createContext<CompanyFilesContextState>({
    name: 'CompanyFilesContext',
  })

export const CompanyFilesProvider = ({ children }: PropsWithChildren) => {
  const {
    state: { id: companyId },
  } = useCompanyDetailContextValue()
  const {
    uploadCompanyFileFlow: { open: openUpload },
    editCompanyFileSchemaFlow: { open: openEditSchema },
  } = useCompanyDetailWorkflowsContextValue()

  const [page, setPage] = useState(0)
  const { data, isPending, isError, refetch, isFetching } =
    useDocumentSignatureTemplatesQuery(companyId, {
      page,
      pageSize: PAGE_SIZE,
    })

  const value: CompanyFilesContextState = {
    state: {
      files: data?.files ?? [],
      totalPages: data?.totalPages ?? 0,
      totalRows: data?.totalRows ?? 0,
      page,
      isPending,
      isError,
      isFetching,
    },
    actions: {
      setPage,
      refetch,
      openUpload,
      openEditSchema,
    },
  }

  return <Context.Provider value={value}>{children}</Context.Provider>
}

export { useCompanyFilesContextValue }
